import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";
import { resetPassword, resetAuthState, loginUser } from "../redux/features/loginSlice";

const ResetPasswordModal = ({ id, isActive, onClose, email }) => {
  const dispatch = useDispatch();
  const { resetPasswordStatus, resetPasswordError } = useSelector(
    (state) => state.auth
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const modalRef = useRef(null);
  const bsModal = useRef(null);

  // Initialize Bootstrap modal
  useEffect(() => {
    if (modalRef.current) {
      bsModal.current = bootstrap.Modal.getOrCreateInstance(modalRef.current, {
        backdrop: true,
        keyboard: true,
      });

      modalRef.current.addEventListener("hidden.bs.modal", () => {
        // Clear all fields when modal closes
        setPassword("");
        setConfirmPassword("");
        setError("");
        setShowPassword(false);
        setShowConfirm(false);

        onClose?.();
      });
    }
    return () => {
      modalRef.current?.removeEventListener("hidden.bs.modal", onClose);
      bsModal.current = null;
    };
  }, [onClose]);

  // Show/hide modal based on isActive
  useEffect(() => {
    if (!bsModal.current) return;
    if (isActive) {
      bsModal.current.show();
    } else {
      bsModal.current.hide();
    }
  }, [isActive]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Basic validations
    if (!password || !confirmPassword) {
      setError("Both fields are required");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    dispatch(resetPassword({ email, password }));
  };

  // After successful reset → auto login
  useEffect(() => {
    if (resetPasswordStatus !== "success") return;

    if (bsModal.current) bsModal.current.hide();

    setTimeout(() => {
      if (email && password) {
        dispatch(loginUser({ email, password }));
      }
      dispatch(resetAuthState());

      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    }, 500);
  }, [resetPasswordStatus, dispatch, email, password]);

  return (
    <div className="modal fade" ref={modalRef} id={id} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Reset Password</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => bsModal.current?.hide()}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              {/* New Password */}
              <div className="mb-3 position-relative">
                <label className="form-label">New Password</label>

                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />

                  {/* Eye Toggle Button */}
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                    }}
                  >
                    <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="mb-3 position-relative">
                <label className="form-label">Confirm Password</label>

                <div className="input-group">
                  <input
                    type={showConfirm ? "text" : "password"}
                    className="form-control"
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />

                  {/* Eye Toggle Button */}
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowConfirm(!showConfirm)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                    }}
                  >
                    <i className={`fa-solid ${showConfirm ? "fa-eye-slash" : "fa-eye"}`} />
                  </button>
                </div>
              </div>

              {error && <div className="text-danger mb-2">{error}</div>}
              {resetPasswordError && <div className="text-danger mb-2">{resetPasswordError}</div>}

              <button type="submit" className="btn btn-primary w-100">
                Reset Password
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
